import React, { useEffect, useState } from "react";
import { Form, Button, message, Spin, Alert, Tabs } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCloudArrowDown, faFloppyDisk, faRotateLeft, faChartPie } from "@fortawesome/free-solid-svg-icons";
import appVersionGateApi from "../../../api/appVersionGateApi";
import OtaRolloutPanel from "./ota-rollout-panel";
import VersionGatePlatformCard, { PLATFORM_META } from "./version-gate-platform-card";
import VersionGateHeaderSummary from "./version-gate-header-summary";

/**
 * Trang gộp HAI thứ hay bị lẫn với nhau, nên tách thành 2 tab:
 *
 *  1. "Nhắc cập nhật từ store" — version BINARY. Đổi ở đây là đẩy user đi tải lại
 *     app ngoài App Store / Google Play.
 *  2. "Bản OTA đang chạy" — bản JS do hot-updater giao thẳng tới máy. Version binary
 *     KHÔNG đổi khi đẩy OTA, nên hai con số này không bao giờ so sánh được với nhau.
 *
 * Gộp chung một trang vì cùng trả lời câu hỏi "máy user đang chạy cái gì", nhưng để
 * chung một mạch cuộn thì rất dễ tưởng chúng là một.
 */

const PLATFORM_KEYS = ["ios", "android"];

// So sánh version x.y.z → -1/0/1 (thiếu cấp coi như 0). Dùng cảnh báo min > latest.
function compareVersions(a, b) {
    const pa = String(a).split(".").map((n) => parseInt(n, 10) || 0);
    const pb = String(b).split(".").map((n) => parseInt(n, 10) || 0);
    for (let i = 0; i < 3; i++) {
        const d = (pa[i] || 0) - (pb[i] || 0);
        if (d !== 0) return d > 0 ? 1 : -1;
    }
    return 0;
}

/** Chuẩn hoá config từ API về đúng shape của Form (thiếu field thì để rỗng). */
function toFormValues(data) {
    const init = {};
    PLATFORM_KEYS.forEach((key) => {
        const g = data?.[key] || {};
        init[key] = {
            enabled: g.enabled !== false,
            latestVersion: g.latestVersion || "",
            minVersion: g.minVersion || "",
            storeUrl: g.storeUrl || "",
            title: g.title || "",
            message: g.message || "",
        };
    });
    return init;
}

function AppVersionGateConfig() {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    // Giữ bản gốc để nút "Khôi phục" trả về đúng cấu hình đang chạy trên server.
    const [saved, setSaved] = useState(null);
    const watched = Form.useWatch([], form);

    useEffect(() => {
        (async () => {
            try {
                const res = await appVersionGateApi.getConfig();
                const init = toFormValues(res.data?.data);
                setSaved(init);
                form.setFieldsValue(init);
            } catch {
                message.error("Không tải được cấu hình");
            } finally {
                setLoading(false);
            }
        })();
    }, [form]);

    const onFinish = async (values) => {
        // Cảnh báo cấu hình ngược: min > latest (force bản cao hơn cả bản nhắc).
        for (const key of PLATFORM_KEYS) {
            const g = values[key] || {};
            if (g.minVersion && g.latestVersion && compareVersions(g.minVersion, g.latestVersion) > 0) {
                message.error(`${PLATFORM_META[key].label}: Min version không được lớn hơn Latest version`);
                return;
            }
        }
        setSaving(true);
        try {
            await appVersionGateApi.updateConfig(values);
            setSaved(values);
            message.success("Đã lưu cấu hình cập nhật app");
        } catch (err) {
            message.error(err?.response?.data?.message || "Lưu thất bại");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="p-8 text-center">
                <Spin />
            </div>
        );
    }

    const storeTab = (
        <Form form={form} layout="vertical" onFinish={onFinish}>
            <Alert
                type="info"
                showIcon
                className="mb-4"
                message="Đổi ở đây app tự nhận, không cần build lại — nhưng user vẫn phải ra store tải bản mới."
                description={
                    <ul className="list-disc pl-4 m-0">
                        <li>
                            <b>Min version</b>: bản thấp hơn bị <b>chặn cứng</b>, không bỏ qua được.
                        </li>
                        <li>
                            <b>Latest version</b>: bản thấp hơn được <b>nhắc nhẹ</b>, vẫn vào app được.
                        </li>
                        <li>Tắt công tắc = không nhắc gì cho nền tảng đó.</li>
                    </ul>
                }
            />

            {/* 1 cột trên laptop hẹp, 2 cột từ xl — form khá cao nên cần đủ bề ngang */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                {PLATFORM_KEYS.map((key) => (
                    <VersionGatePlatformCard key={key} platformKey={key} form={form} />
                ))}
            </div>

            {/* Thanh lưu dính đáy: form dài, cuộn xuống cuối mới thấy nút là bất tiện */}
            <div className="sticky bottom-0 mt-4 py-3 bg-surface/95 backdrop-blur border-t border-line flex gap-2">
                <Button
                    type="primary"
                    htmlType="submit"
                    loading={saving}
                    icon={<FontAwesomeIcon icon={faFloppyDisk} className="mr-2" />}
                >
                    Lưu cấu hình
                </Button>
                <Button
                    icon={<FontAwesomeIcon icon={faRotateLeft} className="mr-2" />}
                    disabled={!saved || saving}
                    onClick={() => form.setFieldsValue(saved)}
                >
                    Khôi phục
                </Button>
            </div>
        </Form>
    );

    return (
        <div className="p-6 max-w-6xl">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
                <div className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faCloudArrowDown} className="text-rose-600" />
                    <h1 className="text-xl font-bold m-0">Phiên bản App</h1>
                </div>
                <VersionGateHeaderSummary values={watched || saved} platformKeys={PLATFORM_KEYS} />
            </div>
            <p className="text-sm text-content-3 mb-4">
                Hai thứ khác nhau: <b>bản trên store</b> (phải tải lại app) và <b>bản OTA</b> (tự
                về trong lần mở app kế tiếp).
            </p>

            <Tabs
                defaultActiveKey="store"
                items={[
                    {
                        key: "store",
                        label: (
                            <span>
                                <FontAwesomeIcon icon={faCloudArrowDown} className="mr-2" />
                                Nhắc cập nhật từ store
                            </span>
                        ),
                        children: storeTab,
                    },
                    {
                        key: "ota",
                        label: (
                            <span>
                                <FontAwesomeIcon icon={faChartPie} className="mr-2" />
                                Bản OTA đang chạy
                            </span>
                        ),
                        children: <OtaRolloutPanel />,
                    },
                ]}
            />
        </div>
    );
}

export default AppVersionGateConfig;
